import React, { useState } from 'react';
import { Image, Modal, StyleSheet, TouchableOpacity, TouchableWithoutFeedback, View } from 'react-native';
import { Text } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAppColors } from '../../hooks/useAppColors';
import { responsiveContentStyle } from '../../hooks/useResponsiveLayout';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { useRoleStore } from '../../stores/roleStore';
import { useThemeStore } from '../../stores/themeStore';
import { useDependents } from '../../hooks/useFamilies';

export default function AppHeader() {
  const { t } = useTranslation();
  const colors = useAppColors();
  const theme = useThemeStore((s) => s.mode);
  const insets = useSafeAreaInsets();
  const user = useAuthStore((s) => s.user);
  const activeRole = useRoleStore((s) => s.activeRole);
  const activeProfile = useRoleStore((s) => s.activeProfile);
  const availableRoles = useRoleStore((s) => s.availableRoles);
  const setActiveRole = useRoleStore((s) => s.setActiveRole);
  const setActiveProfile = useRoleStore((s) => s.setActiveProfile);
  const userId = user?.id || '';
  const [menuOpen, setMenuOpen] = useState(false);

  const { data: dependents } = useDependents(
    activeRole === 'guardian' ? userId : ''
  );

  const displayName = activeProfile
    ? activeProfile.name
    : user
    ? `${user.first_name} ${user.last_name}`
    : 'Athlete';

  const isGuardian = activeRole === 'guardian';
  const hasSwitchOptions =
    (isGuardian && dependents && dependents.length > 0) ||
    availableRoles.length > 1;

  const handleSelectSelf = () => {
    setActiveProfile(null);
    setMenuOpen(false);
  };

  const handleSelectChild = (child: any) => {
    const athlete = child.athlete || child;
    setActiveProfile({
      id: child.athlete_id || athlete.id,
      name: `${athlete.first_name} ${athlete.last_name}`,
    });
    setMenuOpen(false);
  };

  const handleSwitchRole = (role: typeof activeRole) => {
    setActiveRole(role);
    setActiveProfile(null);
    setMenuOpen(false);
  };

  return (
    <View
      style={[
        styles.container,
        {
          paddingTop: insets.top + 12,
          backgroundColor: colors.bgHeader,
          borderBottomColor: colors.border,
        },
      ]}
    >
      <View style={[styles.innerContainer, responsiveContentStyle]}>
        <View style={styles.left}>
          <Image
            source={
              theme === 'dark'
                ? require('../../../assets/images/cbjj-logo-white.png')
                : require('../../../assets/images/cbjj-logo-black.png')
            }
            style={styles.logo}
            resizeMode="contain"
          />
        </View>
        <TouchableOpacity
          style={[
            styles.profileSwitcher,
            { backgroundColor: colors.card, borderColor: colors.border },
          ]}
          activeOpacity={0.7}
          onPress={hasSwitchOptions ? () => setMenuOpen(true) : undefined}
        >
          <Icon name="account-circle" size={18} color={colors.textSecondary} />
          <Text
            style={[styles.profileName, { color: colors.text }]}
            numberOfLines={1}
          >
            {displayName}
          </Text>
          {hasSwitchOptions && (
            <Icon name="chevron-down" size={14} color={colors.textTertiary} />
          )}
        </TouchableOpacity>
      </View>

      {/* Profile / Role Switcher Modal */}
      <Modal
        visible={menuOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setMenuOpen(false)}
      >
        <TouchableWithoutFeedback onPress={() => setMenuOpen(false)}>
          <View style={styles.overlay} testID="modal-overlay">
            <TouchableWithoutFeedback>
              <View
                style={[
                  styles.menu,
                  {
                    backgroundColor: colors.card,
                    borderColor: colors.border,
                    marginTop: insets.top + 80,
                  },
                ]}
              >
                {/* Role switching for multi-role users */}
                {availableRoles.length > 1 && (
                  <>
                    <Text style={[styles.menuSection, { color: colors.textTertiary }]}>
                      {t('header.switchRole')}
                    </Text>
                    {availableRoles.map((role) => (
                      <TouchableOpacity
                        key={role}
                        style={[
                          styles.menuItem,
                          activeRole === role && { backgroundColor: colors.border },
                        ]}
                        onPress={() => handleSwitchRole(role)}
                      >
                        <Icon
                          name={
                            role === 'guardian'
                              ? 'account-child'
                              : role === 'coach'
                              ? 'whistle'
                              : 'account'
                          }
                          size={18}
                          color={activeRole === role ? colors.text : colors.textSecondary}
                        />
                        <Text
                          style={[
                            styles.menuItemText,
                            {
                              color: activeRole === role ? colors.text : colors.textSecondary,
                              fontWeight: activeRole === role ? '700' : '400',
                            },
                          ]}
                        >
                          {t(`auth.role${role.charAt(0).toUpperCase() + role.slice(1)}`)}
                        </Text>
                        {activeRole === role && (
                          <Icon name="check" size={16} color={colors.text} />
                        )}
                      </TouchableOpacity>
                    ))}
                  </>
                )}

                {/* Child switching for guardians */}
                {isGuardian && dependents && dependents.length > 0 && (
                  <>
                    <Text style={[styles.menuSection, { color: colors.textTertiary }]}>
                      {t('header.viewingAs')}
                    </Text>
                    <TouchableOpacity
                      style={[
                        styles.menuItem,
                        !activeProfile && { backgroundColor: colors.border },
                      ]}
                      onPress={handleSelectSelf}
                    >
                      <Icon
                        name="account"
                        size={18}
                        color={!activeProfile ? colors.text : colors.textSecondary}
                      />
                      <Text
                        style={[
                          styles.menuItemText,
                          {
                            color: !activeProfile ? colors.text : colors.textSecondary,
                            fontWeight: !activeProfile ? '700' : '400',
                          },
                        ]}
                      >
                        {user ? `${user.first_name} ${user.last_name}` : 'Self'} {t('header.selfSuffix')}
                      </Text>
                      {!activeProfile && <Icon name="check" size={16} color={colors.text} />}
                    </TouchableOpacity>
                    {dependents.map((child: any) => {
                      const athlete = child.athlete || child;
                      const childId = child.athlete_id || athlete.id;
                      const childName = `${athlete.first_name} ${athlete.last_name}`;
                      const isActive = activeProfile?.id === childId;
                      return (
                        <TouchableOpacity
                          key={childId}
                          style={[
                            styles.menuItem,
                            isActive && { backgroundColor: colors.border },
                          ]}
                          onPress={() => handleSelectChild(child)}
                        >
                          <Icon
                            name="account-child"
                            size={18}
                            color={isActive ? colors.text : colors.textSecondary}
                          />
                          <Text
                            style={[
                              styles.menuItemText,
                              {
                                color: isActive ? colors.text : colors.textSecondary,
                                fontWeight: isActive ? '700' : '400',
                              },
                            ]}
                          >
                            {childName}
                          </Text>
                          {isActive && <Icon name="check" size={16} color={colors.text} />}
                        </TouchableOpacity>
                      );
                    })}
                  </>
                )}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  innerContainer: {
    paddingHorizontal: 24,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  left: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 100,
    height: 32,
  },
  profileSwitcher: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderRadius: 20,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderWidth: 1,
    gap: 6,
    marginLeft: -4,
  },
  profileName: {
    fontSize: 13,
    fontWeight: '600',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  menu: {
    marginHorizontal: 24,
    borderRadius: 16,
    borderWidth: 1,
    padding: 8,
  },
  menuSection: {
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 1,
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 4,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 10,
  },
  menuItemText: {
    flex: 1,
    fontSize: 14,
  },
});
